import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Divider,
  Grid,
  Paper,
  Typography,
  useTheme,
  Pagination,
  Stack,
  CircularProgress,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import InfoRequestCard from "./components/InfoRequestCard";

import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import AssignmentIcon from "@mui/icons-material/Assignment";
import StatsCard from "./components/StatsCard";
import DashboardMainCard from "./components/DasboardMainCard";
import MostActiveTopicsChart from "./components/MostActiveTopicsChart";
import TopContributorsChart from "./components/TopContributorsChart";
import CategoryTopicsChart from "./components/CategoryTopicsChart";
import CustomHeaderButton from "pages/Topics/Components/CustomHeaderButton/CustomHeaderButton";
import GridViewIcon from "@mui/icons-material/GridView";
import ListIcon from "@mui/icons-material/List";
import { MuiTable, StatusTag } from "components";
import { dateParse } from "Utils";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DashboardServices from "services/DashboardServices";
import TopicServices from "services/TopicServices";
import { useNavigate } from "react-router-dom";

// Legacy config removed; cards are now built dynamically from API response

const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.auth.user);
  const isAdmin = currentUser?.roles?.some(
    (role) => role.toLowerCase() === "admin",
  );
  const isLight = theme.palette.mode === "light";

  // Glassy background, border, shadow
  const glassBg = isLight ? "rgba(255,255,255,0.85)" : "rgba(34,45,59,0.82)";
  const borderColor = isLight ? "rgba(2,89,165,0.12)" : "rgba(0,188,212,0.18)";
  const boxShadow = isLight
    ? "0 8px 32px 0 rgba(2,89,165,0.10), 0 3px 16px 0 rgba(0,0,0,0.06)"
    : "0 8px 32px 0 rgba(0,188,212,0.12), 0 3px 16px 0 rgba(0,0,0,0.23)";

  const [view, setView] = useState("list");

  // Removed filters state - not used for contribution pagination

  // Stats counters populated from /api/dashboard
  const [statsCounts, setStatsCounts] = useState({
    topics: 0,
    journalists: 0,
    pending: 0,
    acknowledged: 0,
    contributions: 0,
  });
  const [dashboardScope, setDashboardScope] = useState(null);
  const [usersByType, setUsersByType] = useState({});
  const [topicsBreakdown, setTopicsBreakdown] = useState({});
  const [contributionsBreakdown, setContributionsBreakdown] = useState({});
  const [infoRequests, setInfoRequests] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [myContributionsData, setMyContributionsData] = useState([]);
  const [loadingMyContributions, setLoadingMyContributions] = useState(false);
  const [myContributionsPage, setMyContributionsPage] = useState(1);
  const myContributionsLimit = 4;
  const [myContributionsTotalPages, setMyContributionsTotalPages] = useState(1);
  const [myContributionsTotalCount, setMyContributionsTotalCount] = useState(0);

  // Most Active Topics state
  const [mostActiveTopicsData, setMostActiveTopicsData] = useState([]);
  const [loadingMostActiveTopics, setLoadingMostActiveTopics] = useState(false);

  // Top Contributors state
  const [topContributorsData, setTopContributorsData] = useState([]);
  const [loadingTopContributors, setLoadingTopContributors] = useState(false);

  // Category-wise Topic Count state
  const [categoryTopicsData, setCategoryTopicsData] = useState([]);
  const [loadingCategoryTopics, setLoadingCategoryTopics] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchDashboard = async () => {
      try {
        const res = await DashboardServices.get();
        const view = res?.data?.view || {};
        const scope = view?.scope;
        setDashboardScope(scope);

        // Map API fields to our stat keys
        const topicsTotal = view?.topics?.total ?? 0;
        const journalistsCount =
          scope === "admin" ? (view?.users?.total ?? 0) : 0;
        const byType = scope === "admin" ? (view?.users?.byType ?? {}) : {};
        setUsersByType(byType);

        const pendingCount =
          scope === "admin"
            ? (view?.topics?.withoutContributions ?? 0)
            : (view?.topics?.notContributed ?? 0);
        const acknowledgedCount =
          scope === "admin"
            ? (view?.topics?.withContributions ?? 0)
            : (view?.topics?.contributed ?? 0);

        // Contributions data
        const contributionsTotal = view?.contributions?.total ?? 0;
        const contributionsBreakdownData = {};
        if (scope === "admin") {
          contributionsBreakdownData[t("dashboard.activeDiscussions")] =
            view?.contributions?.withCommunications ?? 0;
          contributionsBreakdownData[t("dashboard.pendingDiscussions")] =
            view?.contributions?.withoutCommunications ?? 0;
        } else {
          contributionsBreakdownData[t("dashboard.activeDiscussions")] =
            view?.contributions?.withDiscussions ?? 0;
          contributionsBreakdownData[t("dashboard.pendingResponse")] =
            view?.contributions?.withoutUserReply ?? 0;
        }

        if (isMounted) {
          setStatsCounts({
            topics: topicsTotal,
            journalists: journalistsCount,
            pending: pendingCount,
            acknowledged: acknowledgedCount,
            contributions: contributionsTotal,
          });
          setTopicsBreakdown({
            [t("dashboard.withContributions")]: acknowledgedCount,
            [t("dashboard.openToContribute")]: pendingCount,
          });
          setContributionsBreakdown(contributionsBreakdownData);
        }
      } catch (e) {
        // Keep defaults (zeros) on failure; optionally log
        console.error("Failed to load dashboard stats", e);
      }
    };

    fetchDashboard();
    return () => {
      isMounted = false;
    };
  }, [t]);

  useEffect(() => {
    if (dashboardScope === null) return;

    let isMounted = true;
    const fetchInfoRequests = async () => {
      setLoadingTopics(true);
      try {
        const limit = isAdmin ? 8 : 4;
        const res = await TopicServices.getAll({ limit, page: 1 });
        if (isMounted && res?.success && res?.data) {
          const topics = res.data.view.data || [];
          const filtered = isAdmin
            ? topics
            : topics.filter((t) => t.status?.toLowerCase() === "active");
          setInfoRequests(filtered);
        }
      } catch (e) {
        console.error("Failed to load information requests", e);
      } finally {
        if (isMounted) {
          setLoadingTopics(false);
        }
      }
    };

    fetchInfoRequests();
    return () => {
      isMounted = false;
    };
  }, [dashboardScope, isAdmin]);

  useEffect(() => {
    let isMounted = true;
    const fetchMyContributions = async () => {
      setLoadingMyContributions(true);
      try {
        const res = await TopicServices.getAll({
          limit: myContributionsLimit,
          page: myContributionsPage,
          my_contributions: true,
        });
        if (isMounted && res?.success && res?.data) {
          const responseData = res.data.view || res.data;
          setMyContributionsData(responseData.data || []);
          setMyContributionsTotalPages(responseData.totalPages || 1);
          setMyContributionsTotalCount(
            responseData.totalItems || responseData.total || 0,
          );
        }
      } catch (e) {
        console.error("Failed to load my contributions", e);
      } finally {
        if (isMounted) {
          setLoadingMyContributions(false);
        }
      }
    };

    fetchMyContributions();
    return () => {
      isMounted = false;
    };
  }, [myContributionsPage, myContributionsLimit]);

  // Fetch Most Active Topics
  useEffect(() => {
    let isMounted = true;
    const fetchMostActiveTopics = async () => {
      setLoadingMostActiveTopics(true);
      try {
        const params = {
          limit: 5,
          topic_status: "Active",
          contribution_status: "Active",
        };
        const res = await DashboardServices.getMostActiveTopics(params);
        if (isMounted && res?.success && res?.data?.view?.data) {
          setMostActiveTopicsData(res.data.view.data);
        }
      } catch (e) {
        console.error("Failed to load most active topics", e);
      } finally {
        if (isMounted) {
          setLoadingMostActiveTopics(false);
        }
      }
    };

    fetchMostActiveTopics();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch Top Contributors
  useEffect(() => {
    let isMounted = true;
    const fetchTopContributors = async () => {
      setLoadingTopContributors(true);
      try {
        const params = {
          limit: 10,
          contribution_status: "Active",
          employee_status: "Active",
          topic_status: "Active",
        };
        const res = await DashboardServices.getTopContributors(params);
        if (isMounted && res?.success && res?.data?.view?.data) {
          setTopContributorsData(res.data.view.data);
        }
      } catch (e) {
        console.error("Failed to load top contributors", e);
      } finally {
        if (isMounted) {
          setLoadingTopContributors(false);
        }
      }
    };

    fetchTopContributors();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch Category-wise Topic Count
  useEffect(() => {
    let isMounted = true;
    const fetchCategoryTopics = async () => {
      setLoadingCategoryTopics(true);
      try {
        const params = {
          topic_status: "Active",
          category_status: "Active",
          includeEmpty: false,
        };
        const res = await DashboardServices.getCategoryWiseTopicCount(params);
        if (isMounted && res?.success && res?.data?.view?.data) {
          setCategoryTopicsData(res.data.view.data);
        }
      } catch (e) {
        console.error("Failed to load category topics", e);
      } finally {
        if (isMounted) {
          setLoadingCategoryTopics(false);
        }
      }
    };

    fetchCategoryTopics();
    return () => {
      isMounted = false;
    };
  }, []);

  // Removed setPage and setPageSize - not used for contribution pagination

  return (
    <Box>
      <Grid container spacing={3} mt={2}>
        <Grid item size={{ xs: 12 }}>
          <Grid container spacing={2}>
            {(() => {
              const cards = [];
              // Topics card with contributions and pending breakdown
              cards.push({
                key: "topics",
                label: t("dashboard.totalTopics"),
                count: statsCounts.topics,
                icon: <ForumOutlinedIcon fontSize="large" />,
                breakdown: topicsBreakdown,
                variant: undefined,
              });

              // Users card (show only if users data present e.g., admin scope)
              if (
                dashboardScope === "admin" &&
                (statsCounts.journalists > 0 ||
                  Object.keys(usersByType).length > 0)
              ) {
                cards.push({
                  key: "users",
                  label: t("journalists.totalUsers"),
                  count: statsCounts.journalists,
                  icon: <PersonOutlinedIcon fontSize="large" />,
                  breakdown: usersByType,
                  variant: "users",
                });
              }

              // Contributions card
              if (
                statsCounts.contributions > 0 ||
                Object.keys(contributionsBreakdown).length > 0
              ) {
                cards.push({
                  key: "contributions",
                  label: t("dashboard.totalContributions"),
                  count: statsCounts.contributions,
                  icon: <AssignmentIcon fontSize="large" />,
                  breakdown: contributionsBreakdown,
                  variant: "contributions",
                });
              }

              return cards.map((c) => (
                <Grid
                  item
                  size={{ xs: 12, md: dashboardScope === "admin" ? 4 : 6 }}
                  key={c.key}
                >
                  <StatsCard
                    label={c.label}
                    count={c.count}
                    icon={c.icon}
                    breakdown={c.breakdown}
                    variant={c.variant}
                  />
                </Grid>
              ));
            })()}
          </Grid>
        </Grid>

        {/* Most Active Topics Chart - Show for admin users */}
        {dashboardScope === "admin" && (
          <Grid item size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }}>
            <MostActiveTopicsChart
              data={mostActiveTopicsData}
              loading={loadingMostActiveTopics}
            />
          </Grid>
        )}

        {/* Top Contributors Chart - Show for admin users */}
        {dashboardScope === "admin" && (
          <Grid item size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }}>
            <TopContributorsChart
              data={topContributorsData}
              loading={loadingTopContributors}
            />
          </Grid>
        )}

        {/* Category Topics Chart - Show for admin users */}
        {dashboardScope === "admin" && (
          <Grid item size={{ xs: 12, sm: 12, md: 12, lg: 4, xl: 4 }}>
            <CategoryTopicsChart
              data={categoryTopicsData}
              loading={loadingCategoryTopics}
            />
          </Grid>
        )}

        {/* Information Requests */}
        <Grid item size={{ xs: 12 }}>
          <DashboardMainCard
            title={t("topics.allTopics")}
            showButton={true}
            buttonLabel={t("dashboard.viewAll")}
            onButtonClick={() => navigate("/all_topics")}
            minHeight={300}
            loading={loadingTopics}
          >
            <Grid container spacing={2} sx={{ py: 2 }}>
              {infoRequests.length === 0 && !loadingTopics ? (
                <Grid item size={{ xs: 12 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: { xs: "0.85rem", md: "0.875rem" },
                      textAlign: "center",
                      py: 4,
                    }}
                  >
                    {t("topics.noTopicsFound")}
                  </Typography>
                </Grid>
              ) : (
                infoRequests.map((req) => (
                  <Grid item size={{ xs: 12, md: 6, lg: 3 }} key={req.id}>
                    <InfoRequestCard {...req} />
                  </Grid>
                ))
              )}
            </Grid>
          </DashboardMainCard>
        </Grid>

        {/* My Contributions - Hidden for admin users */}
        {dashboardScope !== "admin" && (
          <Grid item size={{ xs: 12 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 4,
                minHeight: 120,
                background: glassBg,
                boxShadow: boxShadow,
                border: `1.2px solid ${borderColor}`,
                backdropFilter: "blur(7px)",
                transition: "box-shadow 0.22s, transform 0.17s",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "1.15rem", md: "1.3rem" },
                    letterSpacing: 0.1,
                    color: theme.palette.text.primary,
                  }}
                >
                  {t("dashboard.myContributions")}
                </Typography>

                <Grid
                  item
                  size={{ xs: 12, md: 3 }}
                  display="flex"
                  justifyContent={{ xs: "center", md: "flex-end" }}
                >
                  <Box display="flex">
                    <CustomHeaderButton
                      active={view === "grid"}
                      onClick={() => setView("grid")}
                      icon={<GridViewIcon />}
                      tooltip={t("topics.gridView")}
                    />
                    <CustomHeaderButton
                      active={view === "list"}
                      onClick={() => setView("list")}
                      icon={<ListIcon />}
                      tooltip={t("topics.listView")}
                    />
                  </Box>
                </Grid>
              </Box>

              <Divider
                sx={{ mb: 2, borderColor: isLight ? "#e3eaf3" : "#2c3a4e" }}
              />

              {view === "grid" ? (
                <>
                  {loadingMyContributions ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      minHeight={200}
                    >
                      <CircularProgress />
                    </Box>
                  ) : myContributionsData.length === 0 ? (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: { xs: "0.85rem", md: "0.875rem" },
                        m: 2,
                        textAlign: "center",
                      }}
                    >
                      {t("dashboard.noContributions")}
                    </Typography>
                  ) : (
                    <>
                      <Grid container spacing={2}>
                        {myContributionsData.map((contribution) => (
                          <Grid
                            item
                            size={{ xs: 12, md: 6, lg: 3 }}
                            key={contribution.id}
                          >
                            <InfoRequestCard {...contribution} />
                          </Grid>
                        ))}
                      </Grid>
                      {myContributionsTotalPages > 1 && (
                        <Stack
                          direction="row"
                          justifyContent="center"
                          sx={{ mt: 3 }}
                        >
                          <Pagination
                            count={myContributionsTotalPages}
                            page={myContributionsPage}
                            onChange={(event, value) =>
                              setMyContributionsPage(value)
                            }
                            color="primary"
                            shape="rounded"
                            size="large"
                            showFirstButton
                            showLastButton
                            sx={{
                              "& .MuiPaginationItem-root": {
                                borderRadius: 2,
                                fontWeight: 500,
                                fontSize: {
                                  xs: "0.8rem",
                                  sm: "0.875rem",
                                  md: "0.95rem",
                                },
                                minWidth: { xs: 32, sm: 36, md: 40 },
                                height: { xs: 32, sm: 36, md: 40 },
                                border: `1.5px solid ${
                                  isLight
                                    ? "rgba(2,89,165,0.12)"
                                    : "rgba(0,188,212,0.18)"
                                }`,
                                background: isLight
                                  ? "rgba(255,255,255,0.7)"
                                  : "rgba(34,45,59,0.7)",
                                backdropFilter: "blur(4px)",
                                transition: "all 0.2s",
                                "&:hover": {
                                  background: isLight
                                    ? "rgba(2,89,165,0.08)"
                                    : "rgba(0,188,212,0.10)",
                                  transform: "translateY(-2px)",
                                  boxShadow: isLight
                                    ? "0 4px 12px rgba(2,89,165,0.15)"
                                    : "0 4px 12px rgba(0,188,212,0.15)",
                                },
                                "&.Mui-selected": {
                                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                  color: "#fff",
                                  fontWeight: 600,
                                  border: "none",
                                  boxShadow: isLight
                                    ? "0 4px 14px rgba(2,89,165,0.25)"
                                    : "0 4px 14px rgba(0,188,212,0.25)",
                                  "&:hover": {
                                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                                  },
                                },
                              },
                            }}
                          />
                        </Stack>
                      )}
                    </>
                  )}
                </>
              ) : (
                <Grid container spacing={2}>
                  {myContributionsData.length === 0 &&
                  !loadingMyContributions ? (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: { xs: "0.85rem", md: "0.875rem" },
                        m: 2,
                      }}
                    >
                      {t("dashboard.noContributions")}
                    </Typography>
                  ) : (
                    <MuiTable
                      columns={[
                        {
                          accessorKey: "actions",
                          header: t("common.actions"),
                          Cell: ({ cell }) => (
                            <div>
                              <Tooltip title={t("common.viewDetails")}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => {
                                    navigate(
                                      `/single_topic/${cell.row.original.id}`,
                                    );
                                  }}
                                >
                                  <VisibilityIcon />
                                </IconButton>
                              </Tooltip>
                            </div>
                          ),
                        },
                        {
                          accessorKey: "topic_name",
                          header: t("topics.topic"),
                        },
                        {
                          accessorKey: "category.category_name",
                          header: t("common.category"),
                        },
                        {
                          accessorKey: "createdAt",
                          header: t("common.createdAt"),
                          Cell: ({ cell }) => dateParse(cell.getValue()),
                        },
                        {
                          accessorKey: "updatedAt",
                          header: t("common.updatedAt"),
                          Cell: ({ cell }) => dateParse(cell.getValue()),
                        },
                        {
                          accessorKey: "status",
                          header: t("common.status"),
                          Cell: ({ cell }) => {
                            const status = cell.getValue();
                            return (
                              <StatusTag
                                status={status.toLowerCase()}
                                variant="small"
                              />
                            );
                          },
                        },
                      ]}
                      data={myContributionsData}
                      manualPagination={true}
                      enablePagination={
                        myContributionsTotalCount > myContributionsLimit
                      }
                      rowCount={myContributionsTotalCount || 0}
                      currentPage={myContributionsPage}
                      pageSize={myContributionsLimit}
                      onPageChange={setMyContributionsPage}
                      rowsPerPageOptions={[4]}
                      loading={loadingMyContributions}
                    />
                  )}
                </Grid>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default Dashboard;
